import { useEffect, useRef } from 'react';
import type { Absence, TimelineSlot, VisibleRange } from '../types';
import type { CalendarController } from './controller';
import { toCalendarSelection } from './selection';

type UseTimelineSelectionSyncParams = {
  absences: Absence[];
  activeSelection: TimelineSlot | null;
  calendar: Pick<CalendarController, 'select' | 'unselect'>;
  isDayView: boolean;
  visibleRange: VisibleRange;
};

export default function useTimelineSelectionSync({
  absences,
  activeSelection,
  calendar,
  isDayView,
  visibleRange,
}: UseTimelineSelectionSyncParams) {
  const syncedSelectionRef = useRef<string | null>(null);
  const isSyncingSelectionRef = useRef(false);

  useEffect(() => {
    const selection = toCalendarSelection(activeSelection, absences, isDayView);
    if (!selection) {
      if (syncedSelectionRef.current !== null) {
        isSyncingSelectionRef.current = true;
        calendar.unselect();
        isSyncingSelectionRef.current = false;
        syncedSelectionRef.current = null;
      }
      return;
    }

    // Include the resolved hourly fallback, so loading/changing the referenced
    // record updates the highlight even if the draft itself has not changed.
    const syncKey = JSON.stringify({
      selection,
      visibleRangeStart: visibleRange.start.getTime(),
      visibleRangeEnd: visibleRange.end.getTime(),
    });

    if (syncedSelectionRef.current === syncKey) {
      return;
    }

    isSyncingSelectionRef.current = true;
    if (syncedSelectionRef.current !== null) {
      calendar.unselect();
    }
    calendar.select(selection);

    const frame = window.requestAnimationFrame(() => {
      isSyncingSelectionRef.current = false;
    });
    syncedSelectionRef.current = syncKey;
    return () => {
      window.cancelAnimationFrame(frame);
      isSyncingSelectionRef.current = false;
    };
  }, [absences, activeSelection, calendar, isDayView, visibleRange]);

  return {
    isSyncingSelectionRef,
  };
}
